import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Player } from '@schibsted-svp/web-player';
import { ResolvedPermissionValue } from '@vgtv/consent-manager';

import {
  getDeviceType,
  isAndroid,
  isHermesApp,
  isIOS,
} from '../../../utils/device';
import {
  getAssetAspectRatio,
  getAssetEmbeddedTags,
  getAssetMetadata,
} from '../../../utils/player/asset';
import { formatTagValue } from './formatTagValue';
import { buildConsentKeywords } from '../buildConsentKeywords';
import { getClientKeywords } from '../getClientKeywords';
import { prepareXandrKeywords } from '../prepareKeywords';
import type { AdnSiteConfig, XandrKeywords } from '../types';
import { getPlayerAspectRatio } from '../getPlayerAspectRatio';

function getAppTypeKeyword(): Record<string, string> {
  if (!isHermesApp || (!isIOS && !isAndroid)) {
    return {};
  }
  const deviceType = getDeviceType();

  return {
    'kw_no-sno-apptype': `${isIOS ? 'ios' : 'android'}_${deviceType}_hybrid`,
  } as const;
}

export function getAssetKeywords(
  asset: Asset,
  dynamicAR = false,
  playerAspectRatio?: number
) {
  const categoryTitle = asset.category.title;
  const { contentType } = getAssetMetadata(asset);
  const keywords: Record<
    string,
    string | (string | undefined)[] | boolean | undefined
  > = {
    'kw_aa-sch-content_type': [
      `${formatTagValue(categoryTitle)}_${contentType}`,
      formatTagValue(contentType),
    ],
    'kw_no-sno-news-sponsors': getAssetEmbeddedTags(asset)
      .map(({ tag }) => tag)
      .map(formatTagValue),
  };

  return {
    ...keywords,
    ...getClientKeywords(
      dynamicAR,
      getAssetAspectRatio(asset),
      playerAspectRatio
    ),
  };
}

function addNonSkipKeywords(
  keywords: Partial<XandrKeywords>,
  site: string | undefined
) {
  if (
    site === 'vg' &&
    keywords['kw_aa-sch-page_type'] === 'article' &&
    keywords['kw_aa-sch-instream_player_aspectratio'] === '9-16'
  ) {
    return {
      ...keywords,
      'kw_aa-sch-instream_playercontext': 'stories',
    };
  }

  return keywords;
}

export const getGlobalKeywords = async (
  keywords: Partial<
    Record<
      | 'countryCode'
      | 'supplyType'
      | 'publisherName'
      | 'pageType'
      | 'publisherGroup'
      | 'newsSection',
      string | string[]
    >
  >,
  asset: Asset,
  site: string,
  cmpConsent: ResolvedPermissionValue,
  dynamicAR: boolean,
  player?: Player,
  siteConfig?: AdnSiteConfig<'vgtv'>
) => {
  return prepareXandrKeywords(
    addNonSkipKeywords(
      Object.assign(
        {
          'kw_aa-sch-country_code': keywords.countryCode,
          'kw_aa-sch-supply_type': keywords.supplyType,
          'kw_aa-sch-publisher': keywords.publisherName,
          'kw_aa-sch-page_type': keywords.pageType,
          'kw_no-sno-publishergroup': keywords.publisherGroup,
          'kw_no-sno-news-section': keywords.newsSection,
        },
        getAppTypeKeyword(),
        getAssetKeywords(asset, dynamicAR, getPlayerAspectRatio(player)),
        await buildConsentKeywords({
          player,
          glimrConfig: siteConfig?.glimr,
          cmpConsent,
        })
      ),
      site
    )
  );
};
